/**
 * CustomFieldsSection — generic renderer for a tenant's custom field definitions.
 *
 * Fetches the active definitions for an entity_type and renders typed inputs in
 * display_order, driven entirely by the registry (P1/P2). Controlled: the parent
 * owns the `values` object (the entity's `custom_fields`) and receives the full
 * merged object on every change, matching the full-replace write semantics of
 * the deal update endpoint. See docs/platform/custom-fields-design.md.
 */
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { Skeleton } from '../../../components/ui/skeleton';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { InfoTooltip } from '../common/InfoTooltip';
import { RichTextEditor } from '../common/RichTextEditor';
import { LocalizedNumericInput } from '../LocalizedNumericInput';
import { LocalizedMonetaryInput } from '../LocalizedMonetaryInput';
import { LocalizedDateInput } from '../LocalizedDateInput';
import { useDefaultCurrency } from '../../hooks/useDefaultCurrency';
import { useCustomFieldDefinitions } from './useCustomFieldDefinitions';
import { useOtherRecordTypeFieldCount } from './useOtherRecordTypeFieldCount';
import { HiddenCustomFieldsHint } from './HiddenCustomFieldsHint';
import {
  activeCustomFieldDefinitions,
  hiddenCustomFieldDefinitions,
  resolveCustomFieldLabel,
  resolveCustomFieldOptionLabel,
  visibleCustomFieldDefinitions,
  type CustomFieldErrors,
} from './customFieldUtils';
import type { CustomFieldDefinition } from '../../../generated/api/models/customFieldDefinition';

const NONE_VALUE = '__cf_none__';

/**
 * True when a rich-text HTML payload carries no real content — an empty
 * paragraph the editor emits with formatting/direction attributes
 * (`<p></p>`, `<p dir="auto"></p>`, `<p><br></p>`). Such a value is stored as
 * null so an emptied field round-trips to "no value", not stray markup.
 */
function isRichTextEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() === '';
}

export interface CustomFieldsSectionProps {
  /** Entity registry key (e.g. 'pipeline_deal'). */
  entityType: string;
  /** The entity's current `custom_fields` value. */
  values: Record<string, unknown>;
  /** Receives the full merged custom_fields object on every edit. */
  onChange: (next: Record<string, unknown>) => void;
  /** Per-field errors keyed by definition `key` (client + mapped server errors). */
  errors?: CustomFieldErrors;
  /** Render inputs as disabled (no edits). */
  disabled?: boolean;
  /** Currency code for currency-type fields. Defaults to the viewer's currency. */
  currency?: string;
  /**
   * The entity's relationship classes (Phase 2). When provided, only fields the
   * entity INHERITS from these relationships are shown (union / OR semantics);
   * the visible set updates reactively as the entity's relationships change.
   * Omit for entities that don't carry relationships (deals, products…) — every
   * active definition shows, unchanged.
   */
  relationships?: string[];
  /**
   * Render ONLY the definitions the tenant marked `required`.
   *
   * For a quick-create dialog, whose whole contract is a 3-5 field budget
   * (STYLE_GUIDE § Quick Create Dialog): a required custom field is not an
   * optional extra the detail page can collect later — the create endpoint
   * validates `partial=False` and refuses without it — while an optional one is,
   * so the dialog asks for the required subset and nothing more.
   */
  requiredOnly?: boolean;
  /** What to render when the tenant has no active definitions. Default: hidden. */
  emptyState?: 'hidden' | 'message';
  className?: string;
}

export function CustomFieldsSection({
  entityType,
  values,
  onChange,
  errors,
  disabled,
  currency,
  relationships,
  requiredOnly = false,
  emptyState = 'hidden',
  className,
}: CustomFieldsSectionProps) {
  const { t } = useTranslation(['custom_fields', 'common']);
  const defaultCurrency = useDefaultCurrency();
  const { data, isLoading } = useCustomFieldDefinitions(entityType);

  const definitions = useMemo(() => {
    const visible = visibleCustomFieldDefinitions(
      activeCustomFieldDefinitions(data?.definitions),
      relationships
    );
    return requiredOnly ? visible.filter((d) => d.required) : visible;
  }, [data?.definitions, relationships, requiredOnly]);

  // Fields the record does NOT inherit. A quick-create dialog is deliberately
  // excluded: its whole contract is a 3-5 field budget (STYLE_GUIDE § Quick
  // Create Dialog), so a diagnosis of what is absent has no place in it.
  const hidden = useMemo(
    () =>
      requiredOnly
        ? []
        : hiddenCustomFieldDefinitions(
            activeCustomFieldDefinitions(data?.definitions),
            relationships
          ),
    [data?.definitions, relationships, requiredOnly]
  );

  // `undefined` for a quick-create dialog switches the query off entirely: it
  // renders no hint, so it can never use the answer and must not pay for it.
  const otherCount = useOtherRecordTypeFieldCount(
    entityType,
    requiredOnly ? undefined : relationships
  );

  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-4', className)}>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  // Gate on the hint's CONTENT, never on the element being truthy: it renders
  // null when it has nothing to say, and a truthy-element check would make the
  // `emptyState="message"` branch below unreachable for any relationship-bearing
  // caller. Asking for the count here costs no extra request — the hint's own
  // call names the same `allCustomFieldDefinitionsKey`.
  const hint =
    relationships !== undefined && !requiredOnly && (hidden.length > 0 || otherCount > 0) ? (
      <HiddenCustomFieldsHint
        entityType={entityType}
        relationships={relationships}
        hidden={hidden}
      />
    ) : null;

  if (definitions.length === 0) {
    // Every field is hidden by scope (or defined on the other record type): the
    // hint IS the content, and the only thing on screen that can say why the
    // card looks empty. It REPLACES the empty message rather than joining it,
    // because "No custom fields defined." would be false here — fields are
    // defined, this record just does not inherit them.
    if (hint) return <div className={className}>{hint}</div>;
    if (emptyState === 'message') {
      return (
        <p className={cn('text-sm text-muted-foreground', className)}>
          {t('custom_fields:section.empty')}
        </p>
      );
    }
    return null;
  }

  const setValue = (key: string, value: unknown) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {definitions.map((def) => (
          <CustomFieldControl
            key={def.id}
            definition={def}
            value={values[def.key]}
            error={errors?.[def.key]}
            disabled={disabled}
            currency={currency ?? defaultCurrency}
            onChange={(value) => setValue(def.key, value)}
          />
        ))}
      </div>
      {hint}
    </div>
  );
}

interface CustomFieldControlProps {
  definition: CustomFieldDefinition;
  value: unknown;
  error?: string;
  disabled?: boolean;
  currency: string;
  onChange: (value: unknown) => void;
}

function CustomFieldControl({
  definition,
  value,
  error,
  disabled,
  currency,
  onChange,
}: CustomFieldControlProps) {
  const { t } = useTranslation(['custom_fields', 'common']);
  const fieldId = useId();
  const label = resolveCustomFieldLabel(definition);
  const validation = (definition.validation as Record<string, unknown>) || {};
  const hasError = !!error;

  const labelRow = (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={fieldId} required={definition.required}>
        {label}
      </Label>
      {definition.help_text && <InfoTooltip content={String(definition.help_text)} />}
    </div>
  );

  const errorRow = hasError ? <p className="text-sm text-destructive">{error}</p> : null;

  switch (definition.field_type) {
    case 'boolean':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <div className="flex h-10 items-center">
            <Switch
              id={fieldId}
              checked={value === true}
              onCheckedChange={(checked) => onChange(checked)}
              disabled={disabled}
            />
          </div>
          {errorRow}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <LocalizedNumericInput
            id={fieldId}
            value={typeof value === 'number' ? value : null}
            onChange={(v) => onChange(v)}
            min={typeof validation.min === 'number' ? validation.min : undefined}
            max={typeof validation.max === 'number' ? validation.max : undefined}
            error={hasError}
            disabled={disabled}
          />
          {errorRow}
        </div>
      );

    case 'currency':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <LocalizedMonetaryInput
            id={fieldId}
            value={typeof value === 'number' ? value : null}
            onChange={(v) => onChange(v)}
            currency={currency}
            min={typeof validation.min === 'number' ? validation.min : undefined}
            max={typeof validation.max === 'number' ? validation.max : undefined}
            error={hasError}
            disabled={disabled}
          />
          {errorRow}
        </div>
      );

    case 'date':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <LocalizedDateInput
            id={fieldId}
            value={typeof value === 'string' ? value : null}
            onChange={(v) => onChange(v)}
            minDate={typeof validation.min === 'string' ? validation.min : undefined}
            maxDate={typeof validation.max === 'string' ? validation.max : undefined}
            error={hasError}
            disabled={disabled}
          />
          {errorRow}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <Select
            value={typeof value === 'string' && value ? value : undefined}
            onValueChange={(v) => onChange(v === NONE_VALUE ? null : v)}
            disabled={disabled}
          >
            <SelectTrigger id={fieldId} className={cn(hasError && 'border-destructive')}>
              <SelectValue placeholder={t('custom_fields:placeholder.select')} />
            </SelectTrigger>
            <SelectContent>
              {!definition.required && (
                <SelectItem value={NONE_VALUE}>{t('common:app.none')}</SelectItem>
              )}
              {(definition.options ?? []).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {resolveCustomFieldOptionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorRow}
        </div>
      );

    case 'multi_select':
      return (
        <div className="space-y-1.5">
          {labelRow}
          <MultiSelectControl
            id={fieldId}
            definition={definition}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(v) => onChange(v)}
            disabled={disabled}
            hasError={hasError}
          />
          {errorRow}
        </div>
      );

    case 'rich_text': {
      const currentHtml = typeof value === 'string' ? value : '';
      const currentNorm = isRichTextEmpty(currentHtml) ? null : currentHtml;
      return (
        <div className="space-y-1.5 sm:col-span-2">
          {labelRow}
          <RichTextEditor
            value={currentHtml}
            onChange={(html) => {
              // Ignore no-op echoes: the editor emits an onChange on mount when
              // an extension normalizes the empty doc (e.g. BlockTextDirection
              // adds dir="auto"). Saving that before the real value loads would
              // clobber the stored content via the auto-save debounce. Only
              // propagate a genuine change from the current value.
              const next = isRichTextEmpty(html) ? null : html;
              if (next === currentNorm) return;
              onChange(next);
            }}
            disabled={disabled}
          />
          {errorRow}
        </div>
      );
    }

    case 'text':
    default:
      return (
        <div className="space-y-1.5">
          {labelRow}
          <Input
            id={fieldId}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
            maxLength={typeof validation.max_length === 'number' ? validation.max_length : undefined}
            disabled={disabled}
            className={cn(hasError && 'border-destructive')}
          />
          {errorRow}
        </div>
      );
  }
}

interface MultiSelectControlProps {
  id: string;
  definition: CustomFieldDefinition;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  hasError?: boolean;
}

function MultiSelectControl({
  id,
  definition,
  value,
  onChange,
  disabled,
  hasError,
}: MultiSelectControlProps) {
  const { t } = useTranslation('custom_fields');
  const options = definition.options ?? [];
  const selectedLabels = options
    .filter((o) => value.includes(o.value))
    .map((o) => resolveCustomFieldOptionLabel(o));

  const toggle = (optionValue: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            selectedLabels.length === 0 && 'text-muted-foreground',
            hasError && 'border-destructive'
          )}
        >
          <span className="truncate">
            {selectedLabels.length > 0
              ? selectedLabels.join(', ')
              : t('placeholder.multiSelect')}
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* The `var()` caps the panel to the space Radix measured between the
          trigger and the viewport edge; the checkbox list's own `max-h-60`
          below stays as the design ceiling and shrinks under that cap. */}
      <PopoverContent
        className="flex max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] flex-col p-1"
        align="start"
      >
        {/*
          A checkbox group, NOT a listbox. This used to carry
          role="listbox"/aria-multiselectable with role="option" + aria-selected on
          each <label>, which is invalid ARIA twice over: an `option` may not contain
          interactive content, and a `label` is not an option. It also made each
          row announce its state twice and disagree with itself — the Checkbox
          reports checked/unchecked while aria-selected claims selected/not.
          Each label+Checkbox pair is already a correctly-named checkbox, so the
          right fix was removing the contradictory roles, not adding more ARIA.
        */}
        <div className="min-h-0 max-h-60 flex-1 overflow-y-auto">
          {options.map((option) => {
            const checked = value.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => toggle(option.value, c === true)}
                />
                <span className="flex-1">{resolveCustomFieldOptionLabel(option)}</span>
                {checked && <Check className="h-4 w-4 text-foreground" />}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
